import { useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { utcToZonedTime } from "date-fns-tz";
import { areaByActiveBySortKey } from "../graphql/queries";
import {
  trackCarByActiveByTrackBySortKeyMini,
  trackCarByActiveByTrackByEodSortKeyMini,
} from "../graphql/trackCar/trackCar-queries";
import { aisleByActiveByAreaBySortKeyLocations } from "../graphql/reloadyard/custom-queries";
import { isTrack } from "./locationUtils";

/**
 * Hook for fetching track and track car data from GraphQL.
 *
 * Extracted from useTrackManagement for better separation of concerns.
 * Provides optimized fetching strategies:
 * - Per-track fetching for targeted updates
 * - Snapshot mode with previous-day fallback for EOD reports
 *
 * @param {Object} options - Configuration options
 * @param {string} options.reloadYardId - Current reload yard ID for filtering
 * @param {boolean} [options.useSnapshot=false] - Enable EOD snapshot mode
 * @param {string} [options.snapshotTimeZone=null] - Timezone for date calculations (e.g., "America/Chicago")
 *
 * @returns {Object} Fetch functions
 * @returns {Function} returns.fetchTracksForYard - Fetch all tracks (aisles) for a yard
 * @returns {Function} returns.fetchTrackCars - Fetch cars for a specific track location
 * @returns {Function} returns.getSnapshotDatePrefix - Get date prefix for EOD queries
 *
 * @example
 * const { fetchTracksForYard, fetchTrackCars } = useTrackFetcher({
 *   reloadYardId,
 *   useSnapshot: false,
 * });
 *
 * // Fetch all tracks
 * const tracks = await fetchTracksForYard(reloadYardId);
 *
 * // Fetch cars for specific track
 * const cars = await fetchTrackCars(trackLocationId);
 */
export const useTrackFetcher = ({
  reloadYardId,
  useSnapshot = false,
  snapshotTimeZone = null,
}) => {
  const activeTrackCarQuery = useSnapshot
    ? trackCarByActiveByTrackByEodSortKeyMini
    : trackCarByActiveByTrackBySortKeyMini;

  // Get snapshot date prefix for EOD queries
  const getSnapshotDatePrefix = useCallback(
    (dayOffset = 0) => {
      const timeZone = snapshotTimeZone || "UTC";
      const localNow = utcToZonedTime(new Date(), timeZone);
      if (dayOffset !== 0) {
        localNow.setDate(localNow.getDate() + dayOffset);
      }
      const year = localNow.getFullYear();
      const month = String(localNow.getMonth() + 1).padStart(2, "0");
      const day = String(localNow.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    [snapshotTimeZone],
  );

  // Fetch tracks for a reload yard
  const fetchTracksForYard = useCallback(async (yardId) => {
    if (!yardId) return [];
    const response = await API.graphql(
      graphqlOperation(areaByActiveBySortKey, {
        reloadYardId: yardId,
        activeSortKey: { beginsWith: { active: "true" } },
        limit: 1000,
      }),
    );
    const areas = response?.data?.areaByActiveBySortKey?.items || [];
    const trackArea = areas.find((area) => isTrack(area));
    if (!trackArea) return [];

    const aisleResponse = await API.graphql(
      graphqlOperation(aisleByActiveByAreaBySortKeyLocations, {
        areaId: trackArea.id,
        activeSortKey: { beginsWith: { active: "true" } },
        limit: 1000,
      }),
    );
    const tracks =
      aisleResponse?.data?.aisleByActiveByAreaBySortKey?.items || [];

    // Project first location with area/zone/aisle from parent (no mutation of API response)
    return tracks.map((aisle) => {
      if (!aisle.locations?.items?.[0]) return aisle;
      const loc = aisle.locations.items[0];
      const firstLoc = { ...loc, area: aisle.area, zone: aisle.zone, aisle };
      return {
        ...aisle,
        locations: {
          ...aisle.locations,
          items: [firstLoc, ...aisle.locations.items.slice(1)],
        },
      };
    });
  }, []);

  // Fetch track cars for a specific track location
  const fetchTrackCars = useCallback(
    async (trackLocationId) => {
      if (!trackLocationId) return [];
      const items = [];
      let nextToken = null;
      do {
        const queryInput = useSnapshot
          ? {
              limit: 500,
              nextToken,
              activeEodTrackIdEodSortKey: {
                beginsWith: { active: "true", eodTrackId: trackLocationId },
              },
              reloadYardId: reloadYardId,
            }
          : {
              limit: 500,
              nextToken,
              activeTrackIdSortKey: {
                beginsWith: { active: "true", trackId: trackLocationId },
              },
              reloadYardId: reloadYardId,
            };

        const response = await API.graphql(
          graphqlOperation(activeTrackCarQuery, queryInput),
        );
        const data = useSnapshot
          ? response?.data?.trackCarByActiveByTrackByEodSortKey
          : response?.data?.trackCarByActiveByTrackBySortKey;
        if (data?.items?.length) {
          items.push(...data.items);
        }
        nextToken = data?.nextToken || null;
      } while (nextToken);

      if (!useSnapshot || items.length > 0) return items;

      // Fallback to previous day's snapshot
      const fallbackPrefix = `${getSnapshotDatePrefix(-1)}#`;
      const fallbackItems = [];
      let fallbackToken = null;
      do {
        const fallbackInput = {
          activeEodTrackIdEodSortKey: {
            beginsWith: {
              active: "true",
              eodTrackId: trackLocationId,
              eodSortKey: fallbackPrefix,
            },
          },
          limit: 500,
          nextToken: fallbackToken,
          reloadYardId: reloadYardId,
        };
        const fallbackResponse = await API.graphql(
          graphqlOperation(activeTrackCarQuery, fallbackInput),
        );
        const fallbackData =
          fallbackResponse?.data?.trackCarByActiveByTrackByEodSortKey;
        if (fallbackData?.items?.length) {
          fallbackItems.push(...fallbackData.items);
        }
        fallbackToken = fallbackData?.nextToken || null;
      } while (fallbackToken);

      return fallbackItems;
    },
    [
      reloadYardId,
      useSnapshot,
      getSnapshotDatePrefix,
      activeTrackCarQuery,
    ],
  );

  return {
    fetchTracksForYard,
    fetchTrackCars,
    getSnapshotDatePrefix,
  };
};
